/**
 * /lab/pelican-test, in the same layout language as the hub's gpt-image test:
 * ink hero with the best pelicans → sticky jump nav → podium and board →
 * walls → games → how it works → prompt → FAQ → run your own.
 */
import { useEffect, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CopyButton, PelicanMedia, ResultGrid, ScoreMeter } from './components'
import { formatCost, formatScore, isLabMediaUrl, modelLabel, modelName, modelPath, resultPath, vendorName } from './format'
import { BrowseHub } from './browse'
import { FaceOff, GuessGame } from './games'
import { PelicanArt } from './art'
import { RunPanel } from './RunPanel'
import { useT } from './i18n'
import { displayByModel, featuredRows, isMovingSvg } from './showcase'
import type { LabLeaderRow, LabResult, LabSummary } from './types'
import './pelican-landing.css'

const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`)

function Head({ id, chip, title, lede }: { id: string; chip: string; title: string; lede?: string }) {
  return (
    <div className="pt-head">
      <span className="pt-chip">{chip}</span>
      <h2 id={id}>{title}</h2>
      {lede ? <p>{lede}</p> : null}
    </div>
  )
}

/** Animated SVGs that passed the motion check are the default showcase media.
    A static render is a deliberate fallback, never the first choice. */
function motionFirstByModel(summary: LabSummary): Map<string, LabResult> {
  return displayByModel(summary.leaderboard, [...summary.faceoff_pool, ...summary.featured, ...summary.pool])
}


function Podium({ rows, best, cohort = false }: { rows: LabLeaderRow[]; best: Map<string, LabResult>; cohort?: boolean }) {
  const t = useT()
  const c = t.pelican
  return (
    <ol className={`pt-podium${cohort ? ' pt-popular' : ''}`} aria-label={c.podiumAria}>
      {rows.map((row) => {
        const r = best.get(row.model)
        return (
          <li key={row.model} data-rank={row.rank}>
            <Link to={modelPath(row.model)} className="pt-podium-card">
              <div className="pt-podium-media">{r ? <PelicanMedia result={r} eager /> : <PelicanArt animated={false} />}</div>
              <div className="pt-podium-body">
                <div className="pt-podium-top">
                  <span className="pt-rank">#{row.rank}</span>
                  <span className="pt-vendor mono">{vendorName(row.model) || row.model}{row.sota ? <span className="sota-badge">SOTA</span> : null}</span>
                </div>
                <b className="pt-podium-name">{modelLabel(row.model)}</b>
                <dl className="pt-podium-stats">
                  <div><dt>{c.podiumScore}</dt><dd><span className="pt-big">{row.score != null ? row.score.toFixed(2) : '—'}</span><span className="mono muted">/5</span></dd></div>
                  <div><dt>{c.podiumDraws}</dt><dd>{pct(row.ok_rate)}</dd></div>
                  <div><dt>{c.podiumMoves}</dt><dd>{pct(row.motion_rate)}</dd></div>
                  <div><dt>{c.podiumCost}</dt><dd>{formatCost(row.avg_cost)}</dd></div>
                </dl>
              </div>
            </Link>
          </li>
        )
      })}
    </ol>
  )
}

function Board({ rows, best, justTested }: { rows: LabLeaderRow[]; best: Map<string, LabResult>; justTested: Set<string> }) {
  const t = useT()
  if (!rows.length) return null
  return (
    <div className="table-wrap pt-board" role="region" aria-label={t.pelican.restOfBoard} tabIndex={0}>
      <table className="board">
        <thead><tr>
          <th scope="col">{t.media.rank}</th><th scope="col">{t.media.model}</th><th scope="col">{t.media.overall}</th>
          <th scope="col" className="r">{t.scene['pelican-test'].ok}</th><th scope="col" className="r">{t.media.actuallyMoves}</th>
          <th scope="col" className="r">{t.media.avgCost}</th><th scope="col" className="r">{t.scene['pelican-test'].units}</th>
        </tr></thead>
        <tbody>
          {rows.map((row) => {
            const r = best.get(row.model)
            return (
              <tr key={row.model}>
                <td><span className="rank">{row.rank}</span></td>
                <td>
                  <Link to={modelPath(row.model)} className="model-cell">
                    <span className="pt-thumb" aria-hidden="true">{r?.png ? <img src={r.png} alt="" loading="lazy" decoding="async" /> : modelName(row.model).charAt(0)}</span>
                    <span>
                      <span className="strong">{modelLabel(row.model)}</span>
                      {justTested.has(row.model) ? <span className="pill">{t.media.newPill}</span> : null}
                      <span className="mono small muted block">{vendorName(row.model) || row.model}{row.sota ? <span className="sota-badge">SOTA</span> : null} · {t.common.testers(row.testers)}</span>
                    </span>
                  </Link>
                </td>
                <td className="num">{row.score != null ? <><ScoreMeter value={row.score} /> {row.score.toFixed(2)}</> : '—'}</td>
                <td className="r num">{pct(row.ok_rate)}</td>
                <td className="r num">{pct(row.motion_rate)}</td>
                <td className="r num">{formatCost(row.avg_cost)}</td>
                <td className="r num">{row.results}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function PelicanLandingView({ summary }: { summary: LabSummary }) {
  const t = useT()
  const c = t.pelican
  const justTested = useMemo(() => new Set(summary.just_tested.map((r) => r.model)), [summary])
  const suggested = useMemo(() => summary.leaderboard.map((r) => r.model), [summary])
  const best = useMemo(() => motionFirstByModel(summary), [summary])
  const { hash } = useLocation()
  useEffect(() => { if (hash) document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView() }, [hash])

  const hasResults = summary.stats.results > 0
  const judge = modelLabel(summary.run_limits.judge_model)
  const popularRows = useMemo(() => featuredRows(summary.leaderboard), [summary])
  const featuredMosaic = popularRows.map((row) => best.get(row.model)).filter((r): r is LabResult => Boolean(r))
  const mosaic = (featuredMosaic.length >= 3 ? featuredMosaic : [...best.values()]).slice(0, 5)
  const movingFame = summary.leaderboard.map((row) => best.get(row.model)).filter((r): r is LabResult => Boolean(r && r.score != null && r.score >= 3.5)).slice(0, 8)
  const leader = summary.leaderboard[0]
  const cohort = popularRows.length ? popularRows : summary.leaderboard.slice(0, 4)
  const cohortIds = new Set(cohort.map((row) => row.model))
  const rest = summary.leaderboard.filter((row) => !cohortIds.has(row.model))
  const canGuess = summary.leaderboard.length >= 4
  const canFace = summary.leaderboard.length >= 2
  const stats: Array<[string, string]> = [
    [c.statModels, String(summary.stats.models)],
    [c.statResults, String(summary.stats.results)],
    [c.statTesters, String(summary.stats.testers)],
    [c.statCost, formatCost(summary.stats.avg_cost)],
  ]
  const steps = c.steps.map(([n, title, d]) => [n, title, d || c.judgedBy(judge)] as const)
  const jump: Array<[string, string, boolean]> = [
    ['leaderboard', c.jump.board, hasResults],
    ['fame', c.jump.fame, summary.featured.length > 0],
    ['shame', c.jump.shame, summary.flops.length > 0],
    ['guess', c.jump.guess, hasResults && canGuess],
    ['face-off', c.jump.faceOff, hasResults && canFace],
    ['testers', c.jump.browse, hasResults],
    ['how', c.jump.how, true],
    ['prompt', c.jump.prompt, true],
    ['faq', c.jump.faq, true],
  ]

  return (
    <div className="pt">
      <section className="pt-hero" aria-labelledby="pelican-title">
        <div className="measure pt-hero-grid">
          <div>
            <nav className="pt-crumbs mono" aria-label="Breadcrumb"><a href="/lab/">{c.crumbs[0]}</a> / <a href="/lab/evals">{c.crumbs[1]}</a></nav>
            <p className="eyebrow pt-kicker">{c.kicker}</p>
            <h1 id="pelican-title">{c.h1Lead}<mark>{c.h1Mark}</mark></h1>
            <p className="pt-lede">{c.lede}</p>
            <div className="actions">
              <a href="#run" className="btn btn-lime">{t.landing.runYourOwn} →</a>
              <a href={hasResults ? '#leaderboard' : '#how'} className="btn pt-btn-ghost">{hasResults ? t.landing.seeLeaderboard : t.landing.howItWorks}</a>
            </div>
            <ul className="pt-chips mono">
              <li>{c.chipClassic}</li><li>{c.chipAnimated}</li><li>{c.chipJudge(judge)}</li>
            </ul>
            {hasResults ? (
              <dl className="pt-stats">
                {stats.map(([label, value]) => <div key={label}><dt>{label}</dt><dd className="num">{value}</dd></div>)}
              </dl>
            ) : null}
          </div>
          <div className="pt-media">
            {mosaic.length >= 3 ? (
              <div className="pt-mosaic" data-n={mosaic.length}>
                {mosaic.map((r, i) => (
                  <Link key={r.id} to={resultPath(r.id)} className="pt-tile" data-i={i} aria-label={t.landing.openPelican(modelLabel(r.model))}>
                    <PelicanMedia result={r} eager={i < 3} large={i === 0} />
                    <span className="pt-tile-cap"><b>{modelLabel(r.model)}</b><span className="mono">{r.score != null ? `${formatScore(r.score)}/5` : t.common.noScore} · {isMovingSvg(r) ? c.verifiedMotion : t.common.kind(r.kind)}</span></span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="pt-art"><PelicanArt /></div>
            )}
            <div className="pt-media-foot">
              {leader?.score != null ? (
                <Link to={modelPath(leader.model)} className="pt-badge"><b>{leader.score.toFixed(2)}<small>/5</small></b><span className="mono">{c.topBadge(modelLabel(leader.model))}</span></Link>
              ) : null}
              <span className="pt-media-cap mono">{c.mediaCaption(judge)}</span>
            </div>
          </div>
        </div>
      </section>

      <div className="pt-jump">
        <nav className="measure" aria-label={c.jumpAria}>
          {jump.filter(([, , show]) => show).map(([id, label]) => <a key={id} href={`#${id}`}>{label}</a>)}
          <a href="#run" className="pt-jump-cta">{c.jump.run}</a>
        </nav>
      </div>

      {hasResults ? (
        <>
          <section id="leaderboard" className="pt-section" aria-labelledby="board-title">
            <div className="measure">
              <Head id="board-title" chip={t.landing.leaderboardEyebrow} title={c.boardTitle} lede={c.boardLede} />
              {summary.just_tested.length ? (
                <div className="pt-ticker" aria-label={t.landing.justTested}>
                  <span className="pill">{t.landing.justTested}</span>
                  <div className="pt-ticker-row">
                    {summary.just_tested.map((r) => (
                      <Link key={r.model} to={modelPath(r.model)}>{modelLabel(r.model)} <span className="mono small subtle">{r.score != null ? t.common.score5(r.score) : t.common.noScore}</span></Link>
                    ))}
                  </div>
                </div>
              ) : null}
              <Podium rows={cohort} best={best} cohort />
              {rest.length ? <h3 className="pt-sub">{c.restOfBoard}</h3> : null}
              <Board rows={rest} best={best} justTested={justTested} />
            </div>
          </section>

          {movingFame.length ? (
            <section id="fame" className="pt-section tint" aria-labelledby="fame-title">
              <div className="measure">
                <Head id="fame-title" chip={t.landing.fameEyebrow} title={c.fameTitle} lede={c.fameLede} />
                <ResultGrid items={movingFame} />
              </div>
            </section>
          ) : null}

          {summary.flops.length ? (
            <section id="shame" className="pt-section" aria-labelledby="shame-title">
              <div className="measure">
                <Head id="shame-title" chip={t.landing.shameEyebrow} title={c.shameTitle} lede={c.shameLede} />
                <ResultGrid items={summary.flops} />
              </div>
            </section>
          ) : null}

          {canGuess ? (
            <section id="guess" className="pt-section tint" aria-labelledby="guess-title">
              <div className="measure">
                <Head id="guess-title" chip={t.landing.miniGame} title={c.guessTitle} lede={c.guessLede} />
                <GuessGame pool={summary.pool} />
              </div>
            </section>
          ) : null}

          {canFace ? (
            <section id="face-off" className="pt-section" aria-labelledby="faceoff-title">
              <div className="measure">
                <Head id="faceoff-title" chip={t.landing.faceOffEyebrow} title={c.faceOffTitle} lede={c.faceOffLede} />
                <FaceOff rows={summary.leaderboard} pool={summary.faceoff_pool} />
              </div>
            </section>
          ) : null}

          <section id="testers" className="pt-section tint" aria-labelledby="browse-title">
            <div className="measure">
              <Head id="browse-title" chip={t.landing.browseEyebrow} title={c.browseTitle} lede={c.browseLede} />
              <BrowseHub summary={summary} />
            </div>
          </section>
        </>
      ) : null}

      <section id="how" className="pt-section dark" aria-labelledby="how-title">
        <div className="measure">
          <Head id="how-title" chip={c.howEyebrow} title={hasResults ? c.howBandTitle : c.howTitle} lede={hasResults ? c.howBandLede : c.howLede} />
          <ol className="pt-steps">
            {steps.map(([n, title, d]) => <li key={n}><b className="mono">{n}</b><h3>{title}</h3><p>{d}</p></li>)}
          </ol>
          {summary.dimensions.length ? (
            <>
              <h3 className="pt-sub">{c.scoredOn}</h3>
              <dl className="pt-dims">
                {summary.dimensions.map((d, i) => (
                  <div key={d.key}>
                    <dt className="mono">{String(i + 1).padStart(2, '0')} · {d.source === 'auto' ? t.landing.measured : t.landing.judgeScale}</dt>
                    <dd className="strong">{d.name}</dd>
                    <dd className="small">{d.description}</dd>
                  </div>
                ))}
              </dl>
            </>
          ) : null}
        </div>
      </section>

      <section id="prompt" className="pt-section" aria-labelledby="prompt-title">
        <div className="measure">
          <Head id="prompt-title" chip={t.landing.thePrompt} title={t.landing.stealPrompt} lede={t.landing.promptHint} />
          <div className="grid-2">
            {summary.prompts.map((p) => (
              <div key={p.kind}>
                <div className="code-panel">
                  <div className="code-head"><span>{p.title}</span><CopyButton text={p.text} /></div>
                  <pre>{p.text}</pre>
                </div>
                <p className="small muted">{p.why}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="pt-section tint" aria-labelledby="faq-title">
        <div className="measure pt-faq">
          <div>
            <span className="pt-chip">{c.faqEyebrow}</span>
            <h2 id="faq-title">{c.faqTitle}</h2>
            <a className="inline-link" href="/lab/ideas">{c.faqMore}</a>
          </div>
          <div className="pt-faq-list">
            {c.faq.map(([q, a]) => (
              <details key={q}>
                <summary>{q}<span aria-hidden="true">+</span></summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section id="run" className="pt-section dark" aria-labelledby="run-title">
        <div className="measure">
          <Head id="run-title" chip={t.landing.yourTurn} title={hasResults ? c.runTitleHas : c.runTitleEmpty} lede={c.runLede} />
          <RunPanel suggested={suggested} limits={summary.run_limits} defaultSubject={summary.default_subject} />
        </div>
      </section>
    </div>
  )
}
